/** The deliberately small JSON Schema vocabulary used by browser tools. */
export interface InputSchema {
  readonly type?: "object" | "array" | "string" | "number" | "integer" | "boolean" | "null";
  readonly description?: string;
  readonly examples?: readonly unknown[];
  readonly default?: unknown;
  readonly properties?: Readonly<Record<string, InputSchema>>;
  readonly required?: readonly string[];
  readonly additionalProperties?: false;
  readonly items?: InputSchema;
  readonly anyOf?: readonly InputSchema[];
  readonly enum?: readonly unknown[];
  readonly minLength?: number;
  readonly maxLength?: number;
  readonly minItems?: number;
  readonly maxItems?: number;
  readonly minimum?: number;
  readonly maximum?: number;
  readonly minProperties?: number;
}

export class CanvasInputError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly field?: string,
  ) {
    super(message);
  }
}

export function toolError(error: unknown) {
  const known = error instanceof CanvasInputError;
  const recovery: Record<string, string> = {
    INVALID_INPUT: "Correct the indicated field using the tool schema and examples, then retry.",
    INVALID_RELATIONSHIP:
      "Read the referenced elements; use eligible, distinct live targets and retry.",
    LIMIT_EXCEEDED:
      "Reduce the request size. Split independent work into bounded batches with a fresh read between them.",
    READ_REQUIRED: "Read the current canvas to obtain a new readToken, then rebuild the request.",
    STALE_READ:
      "Read the changed targets and dependencies, then rebuild the patch with a new operationId.",
    OPERATION_MISMATCH:
      "Retry the original request unchanged, or use a new operationId for different input.",
    NOT_FOUND:
      "Read the canvas to resolve a current element ID; do not create a replacement implicitly.",
    READ_ONLY: "Ask the user to open an editable drawing or obtain edit access in the app.",
    LOCKED: "Ask the user to unlock the target or its dependencies in the editor before retrying.",
    NOT_READY:
      "Wait for the editor to load or use drawing_list and drawing_open to open a drawing.",
    ABORTED: "Re-discover tools and read the current canvas before starting a new request.",
  };
  return {
    ok: false as const,
    error: {
      code: known ? error.code : "INTERNAL_ERROR",
      message: known ? error.message : "The canvas operation could not be completed.",
      recovery:
        known && recovery[error.code]
          ? recovery[error.code]
          : "Check the app for an error, then read the current canvas before retrying.",
      ...(known && error.field ? { field: error.field } : {}),
    },
  };
}

export function jsonBytes(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).byteLength;
}

/** Check complexity before serialization, including cycles and non-JSON direct calls. */
export function validateInput(input: unknown, schema: InputSchema, maxBytes = 128 * 1024): void {
  let nodes = 0;
  const seen = new Set<object>();
  const inspect = (value: unknown, depth: number) => {
    if (++nodes > 20000 || depth > 12) {
      throw new CanvasInputError("LIMIT_EXCEEDED", "Input nesting or size exceeds the limit.");
    }
    if (value === null || typeof value === "boolean") return;
    if (typeof value === "string" && value.length <= maxBytes) return;
    if (typeof value === "number" && Number.isFinite(value)) return;
    if (typeof value !== "object" || seen.has(value!)) {
      throw new CanvasInputError("INVALID_INPUT", "Input must contain bounded JSON values.");
    }
    if (!Array.isArray(value) && Object.getPrototypeOf(value) !== Object.prototype) {
      throw new CanvasInputError("INVALID_INPUT", "Input must contain plain JSON objects.");
    }
    seen.add(value!);
    for (const descriptor of Object.values(Object.getOwnPropertyDescriptors(value!))) {
      if (descriptor.get || descriptor.set) {
        throw new CanvasInputError("INVALID_INPUT", "Input must not contain accessor properties.");
      }
      inspect(descriptor.value, depth + 1);
    }
    seen.delete(value!);
  };
  inspect(input, 0);
  if (jsonBytes(input) > maxBytes) {
    throw new CanvasInputError("LIMIT_EXCEEDED", "Input exceeds the byte limit.");
  }
  const check = (value: unknown, rule: InputSchema, path: string): void => {
    const invalid = (expected: string, field = path): never => {
      throw new CanvasInputError("INVALID_INPUT", expected, field);
    };
    if (rule.anyOf) {
      const errors: CanvasInputError[] = [];
      const candidates = rule.anyOf.filter(
        (option) =>
          !value ||
          typeof value !== "object" ||
          Array.isArray(value) ||
          Object.entries(option.properties ?? {}).every(
            ([key, property]) =>
              !property.enum ||
              !Object.hasOwn(value, key) ||
              property.enum.includes((value as Record<string, unknown>)[key]),
          ),
      );
      const matched = (candidates.length ? candidates : rule.anyOf).some((option) => {
        try {
          check(value, option, path);
          return true;
        } catch (error) {
          if (!(error instanceof CanvasInputError)) throw error;
          errors.push(error);
          return false;
        }
      });
      if (!matched) {
        errors.sort((a, b) => (b.field?.length ?? 0) - (a.field?.length ?? 0));
        throw errors[0];
      }
    }
    if (rule.enum && !rule.enum.includes(value))
      invalid(`Expected one of: ${rule.enum.map((item) => JSON.stringify(item)).join(", ")}.`);
    if (rule.type === "null") {
      if (value !== null) invalid("Expected null.");
      return;
    }
    if (rule.type === "object") {
      if (!value || typeof value !== "object" || Array.isArray(value))
        return invalid("Expected an object.");
      const record = value as Record<string, unknown>;
      if (Object.keys(record).length < (rule.minProperties ?? 0))
        invalid(`Expected at least ${rule.minProperties} property.`);
      for (const key of rule.required ?? [])
        if (!Object.hasOwn(record, key)) invalid(`Required field: ${key}.`, `${path}.${key}`);
      for (const [key, child] of Object.entries(record)) {
        const property = rule.properties?.[key];
        if (!property || !Object.hasOwn(rule.properties!, key))
          invalid(
            "Unsupported field. Use only properties declared by this schema.",
            `${path}.${key}`,
          );
        check(child, property!, `${path}.${key}`);
      }
    } else if (rule.type === "array") {
      if (!Array.isArray(value)) return invalid("Expected an array.");
      if (value.length < (rule.minItems ?? 0) || value.length > (rule.maxItems ?? 1000))
        invalid(`Expected ${rule.minItems ?? 0}..${rule.maxItems ?? 1000} array items.`);
      for (let i = 0; i < value.length; i++) check(value[i], rule.items!, `${path}[${i}]`);
    } else if (rule.type === "number" || rule.type === "integer") {
      if (typeof value !== "number" || !Number.isFinite(value))
        return invalid("Expected a finite number.");
      if (rule.type === "integer" && !Number.isInteger(value)) invalid("Expected an integer.");
      if (value < (rule.minimum ?? -Infinity) || value > (rule.maximum ?? Infinity))
        invalid(
          `Expected a number between ${rule.minimum ?? "-Infinity"} and ${rule.maximum ?? "Infinity"}.`,
        );
    } else if (rule.type === "string") {
      if (typeof value !== "string") return invalid("Expected a string.");
      if (value.length < (rule.minLength ?? 0) || value.length > (rule.maxLength ?? 4096))
        invalid(`Expected ${rule.minLength ?? 0}..${rule.maxLength ?? 4096} characters.`);
    } else if (rule.type === "boolean" && typeof value !== "boolean")
      invalid("Expected a boolean.");
  };
  check(input, schema, "input");
}
